import React, { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { startPushLoop } from "./services/sync-push.engine";
import ConflictModal from "./components/sync/ConflictModal";
import { installSecurityLifecycle } from "./db/security-lifecycle";
import { OfflineBanner } from "./components/OfflineBanner";
import { UpdateBanner } from "./components/UpdateBanner";
import DevRoleSwitcher from "./components/dev/DevRoleSwitcher";
import DevQuickLinks from "./components/dev/DevQuickLinks";
import { GlobalBillingBanners } from "./components/billing";
import { LowBalanceBanner } from "./components/credits/LowBalanceBanner";
import { AuthInterceptor } from "./components/AuthInterceptor";

import LoginPage from "./pages/auth/LoginPage";
import SignUpPage from "./pages/auth/SignUpPage";
import ForgotPasswordPage from "./pages/auth/ForgotPasswordPage";
import SetPasswordPage from "./pages/auth/SetPasswordPage";
import { ClinicClosedLoginPage } from "./pages/auth/ClinicClosedLoginPage";
import { FinalDownloadPage } from "./pages/auth/FinalDownloadPage";
import RoleProvisioningPage from "./pages/auth/RoleProvisioningPage";
import QueueStatusPage from "./pages/queue/QueueStatusPage";
import PublicReschedulePage from "./pages/public/PublicReschedulePage";
import DashboardPage from "./pages/dashboard/DashboardPage";
import ReportsPage from "./pages/reports/ReportsPage";
import ExpenseListPage from "./pages/accounting/ExpenseListPage";
import IncomeListPage from "./pages/accounting/IncomeListPage";
import RecurringExpensesPage from "./pages/accounting/RecurringExpensesPage";
import ClinicDetailsPage from "./pages/onboarding/ClinicDetailsPage";
import DocumentUploadPage from "./pages/onboarding/DocumentUploadPage";
import SelectPlanPage from "./pages/onboarding/SelectPlanPage";
import RequireAuth from "./components/RequireAuth";
import RequireAuthenticated from "./components/RequireAuthenticated";
import RequireAdmin from "./components/RequireAdmin";
import RequireRoles from "./components/RequireRoles";
import RequireClinicSelection from "./components/RequireClinicSelection";
import SelectClinicPage from "./pages/clinic/SelectClinicPage";
import RequirePermission from "./components/RequirePermission";
import RequirePlatformAdmin from "./components/RequirePlatformAdmin";
// PlatformLoginPage intentionally disconnected — platform admins use /login
import KycQueuePage from "./pages/platform/KycQueuePage";
import SupportTicketsPage from "./pages/platform/SupportTicketsPage";
import KycReviewDetailPage from "./pages/platform/KycReviewDetailPage";
import OnboardedClinicsPage from "./pages/platform/OnboardedClinicsPage";
import RevenuePage from "./pages/platform/RevenuePage";
import SoapDiagnosisPad from "./pages/visit/SoapDiagnosisPad";
import NinetysDiagnosis from "./pages/visit/NinetysDiagnosis";
import VisitNotesPage from "./pages/visit/VisitNotesPage";
import ContinueDiagnosis from "./pages/visit/ContinueDiagnosis";
import PendingVisitsPage from "./pages/visit/PendingVisitsPage";
import DiagnosisComplete from "./pages/visit/DiagnosisComplete";
import RosterPage from "./pages/roster/RosterPage";
import StartVisitPage from "./pages/visit/StartVisitPage";
import PatientListPage from "./pages/patients/PatientListPage";
import PatientProfilePage from "./pages/patients/PatientProfilePage";
import VitalsTrendPage from "./pages/vitals/VitalsTrendPage";
import { AddPatientProvider, useAddPatient } from "./contexts/AddPatientContext";
import { AddDoctorProvider, useAddDoctor } from "./contexts/AddDoctorContext";
import { BookAppointmentProvider, useBookAppointment } from "./contexts/BookAppointmentContext";
import { SidebarProvider } from "./contexts/SidebarContext";
import { ClinicProvider } from "./contexts/ClinicContext";
import ClinicSwitchOverlay from "./components/layout/ClinicSwitchOverlay";
import { ImportProgressProvider } from "./contexts/ImportProgressContext";
import BackgroundImportBanner from "./components/BackgroundImportBanner";
import { AddPatientModal } from "./components/patients";
import { AddDoctorModal } from "./components/doctors";
import { BookAppointmentModal } from "./components/appointments";
import {
  WebPushPermissionModal,
  shouldPromptWebPush,
} from "./components/notifications/WebPushPermissionModal";
import DoctorListPage from "./pages/doctors/DoctorListPage";
import AppointmentsPage from "./pages/appointments/AppointmentsPage";
import BulkRescheduleScreen from "./pages/appointments/BulkRescheduleScreen";
import AppointmentCalendarPage from "./pages/appointments/AppointmentCalendarPage";
import AppointmentConfirmationPage from "./pages/appointments/AppointmentConfirmationPage";
import ReceptionistQueuePage from "./pages/appointments/ReceptionistQueuePage";
import SettingsPage from "./pages/settings/SettingsPage";
import { ExportJobStatusPage } from "./pages/settings/ExportJobStatusPage";
import CohortReminderPage from "./pages/immunization/CohortReminderPage";
import CreateBillPage from "./pages/billing/CreateBillPage";
import PlatformKycReviewPage from "./pages/platform/PlatformKycReviewPage";
import DoctorPrescriptionsPage from "./pages/visit/DoctorPrescriptionsPage";
import PharmacyComingSoonPage from "./pages/pharmacy/PharmacyComingSoonPage";

const GlobalModals: React.FC = () => {
  const { isAddPatientModalOpen, closeAddPatientModal } = useAddPatient();
  const { isAddDoctorModalOpen, closeAddDoctorModal, addDoctorPrefill } = useAddDoctor();
  const { isBookAppointmentModalOpen, closeBookAppointmentModal } = useBookAppointment();

  // Web Push permission prompt — shown once, right after login (userLoggedIn
  // is dispatched by RequireAuth when authorization passes). Kept on
  // patientCreated/appointmentCreated as fallback for sessions that were
  // already active when this code shipped.
  const [isWebPushModalOpen, setWebPushModalOpen] = useState(false);
  useEffect(() => {
    const maybePrompt = () => {
      if (shouldPromptWebPush()) setWebPushModalOpen(true);
    };
    window.addEventListener("userLoggedIn", maybePrompt);
    window.addEventListener("patientCreated", maybePrompt);
    window.addEventListener("appointmentCreated", maybePrompt);
    return () => {
      window.removeEventListener("userLoggedIn", maybePrompt);
      window.removeEventListener("patientCreated", maybePrompt);
      window.removeEventListener("appointmentCreated", maybePrompt);
    };
  }, []);

  return (
    <>
      <AddPatientModal
        isOpen={isAddPatientModalOpen}
        onClose={closeAddPatientModal}
        onSuccess={() => window.dispatchEvent(new Event("patientCreated"))}
      />
      <AddDoctorModal
        isOpen={isAddDoctorModalOpen}
        onClose={closeAddDoctorModal}
        onSuccess={() => window.dispatchEvent(new Event("doctorCreated"))}
        prefill={addDoctorPrefill}
      />
      <BookAppointmentModal
        isOpen={isBookAppointmentModalOpen}
        onClose={closeBookAppointmentModal}
        onSuccess={() => window.dispatchEvent(new Event("appointmentCreated"))}
      />
      <WebPushPermissionModal
        isOpen={isWebPushModalOpen}
        onClose={() => setWebPushModalOpen(false)}
      />
    </>
  );
};

// Editing now happens in a drawer over the profile page — /patients/:id/edit
// is a legacy URL (bookmarks, old links) that should land on the profile.
const RedirectToPatientProfile: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/patients/${id}`} replace />;
};

const App: React.FC = () => {
  useEffect(() => {
    const teardown = startPushLoop();
    return () => { teardown(); };
  }, []);

  useEffect(() => installSecurityLifecycle(), []);

  return (
    <ImportProgressProvider>
    <ClinicProvider>
    <SidebarProvider>
    <BookAppointmentProvider>
      <AddDoctorProvider>
        <AddPatientProvider>
          <BrowserRouter>
            <AuthInterceptor />
            <OfflineBanner />
            <UpdateBanner />
            <GlobalBillingBanners />
            <LowBalanceBanner />
            <ToastContainer position="top-right" autoClose={3000} />
            <ConflictModal />
            <GlobalModals />
            <ClinicSwitchOverlay />
            <BackgroundImportBanner />
            <div style={{ paddingTop: "var(--offline-banner-h, 0px)" }}>
            {process.env.NODE_ENV === "development" && <DevRoleSwitcher />}
            {process.env.NODE_ENV === "development" && <DevQuickLinks />}
            <Routes>
              {/* Public routes */}
              {/* Public, unauthenticated - token in the path is the credential. */}
              <Route path="/queue-status/:token" element={<QueueStatusPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/signup" element={<SignUpPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/set-password" element={<SetPasswordPage />} />
              {/* Offboarding Phase 2 screens run on mock data until Phase 1's clinic
                  lifecycle lands - reachable in dev builds only, like DevQuickLinks. */}
              {process.env.NODE_ENV === "development" && (
                <>
                  <Route path="/clinic-closed" element={<ClinicClosedLoginPage />} />
                  <Route path="/download/:token" element={<FinalDownloadPage />} />
                </>
              )}
              <Route path="/reschedule" element={<PublicReschedulePage />} />
              <Route element={<RequireAuthenticated />}>
                {/* Role selection removed — self-signup auto-provisions Admin. */}
                <Route path="/auth/role" element={<RoleProvisioningPage />} />
              </Route>

              <Route element={<RequireAuth />}>
                {/* Platform Admin — KYC review tool (no clinic nav, no onboarding) */}
                <Route element={<RequirePlatformAdmin />}>
                  <Route path="/platform/clinics" element={<OnboardedClinicsPage />} />
                  <Route path="/platform/kyc" element={<KycQueuePage />} />
                  <Route path="/platform/kyc/:id" element={<KycReviewDetailPage />} />
                  <Route path="/platform/revenue" element={<RevenuePage />} />
                  <Route path="/platform/support-tickets" element={<SupportTicketsPage />} />
                </Route>

                <Route
                  path="/onboarding"
                  element={<Navigate to="/onboarding/clinic-details" replace />}
                />
                <Route path="/onboarding/clinic-details" element={<ClinicDetailsPage />} />
                <Route path="/onboarding/documents" element={<DocumentUploadPage />} />
                <Route path="/onboarding/select-plan" element={<SelectPlanPage />} />
                {/* PlatformAdmin: cross-clinic KYC review — the ONLY app area they may reach. */}
                <Route element={<RequireRoles roles={["PlatformAdmin"]} />}>
                  <Route path="/platform/kyc" element={<PlatformKycReviewPage />} />
                </Route>

                {/* Clinic app — clinic staff only. A PlatformAdmin (no clinic, no patient/
                    clinical access per the Role Access Matrix) is redirected to /platform/kyc
                    even on a manually typed URL — not merely hidden from the sidebar nav. */}
                {/* Reachable directly so an owner can re-pick without signing out. */}
                <Route path="/select-clinic" element={<SelectClinicPage />} />

                <Route element={<RequireRoles roles={["Admin", "Doctor", "Receptionist"]} />}>
                  {/* A multi-clinic owner picks a clinic before the app opens.
                      Inside RequireRoles (auth + role already settled) and
                      OUTSIDE the onboarding routes above, which have a clinic
                      chosen already and would fight the picker. */}
                  <Route element={<RequireClinicSelection />}>
                  {/* Dashboard: all clinic roles see it; content switches per role inside. */}
                  <Route path="/dashboard" element={<DashboardPage />} />
                  {/* Reports + raw revenue data: Admin only. */}
                  <Route element={<RequireAdmin />}>
                    {/* The page's endpoints are still Admin role-gated server-side,
                        so the grant narrows Admin rather than replacing the check. */}
                    <Route element={<RequirePermission codes={["reports.view_clinic"]} label="Reports" legacyRoles={["Admin"]} />}>
                      <Route path="/reports" element={<ReportsPage />} />
                    </Route>
                  </Route>
                  {/* Accounting module (expenses, other income, recurring templates):
                      Admin-only, no plan/feature-flag gating — see
                      PROGRESS_accounting_module.md §0.4. */}
                  <Route element={<RequireAdmin />}>
                    <Route path="/accounting/expenses" element={<ExpenseListPage />} />
                    <Route path="/accounting/income" element={<IncomeListPage />} />
                    <Route path="/accounting/recurring-expenses" element={<RecurringExpensesPage />} />
                  </Route>
                  {/* Clinical flows: Receptionist cannot access these */}
                  <Route element={<RequireRoles roles={["Admin", "Doctor"]} />}>
                    {/* The role gate stays (Receptionist never reaches clinical
                        screens); the grant is what makes the Clinical checkboxes
                        in the Role Catalog actually remove these pages. The
                        whole visit pad is one flow that starts or continues a
                        visit, so it is gated on the write, like the endpoints. */}
                    <Route element={<RequirePermission codes={["visits.write"]} label="Diagnosis" />}>
                      <Route path="/soap-diagnosis-pad" element={<SoapDiagnosisPad />} />
                      <Route path="/90sdiagnosis" element={<NinetysDiagnosis />} />
                      <Route path="/continue-diagnosis" element={<ContinueDiagnosis />} />
                      <Route path="/pending-visits" element={<PendingVisitsPage />} />
                      <Route path="/diagnosis-complete" element={<DiagnosisComplete />} />
                      <Route path="/start-visit" element={<StartVisitPage />} />
                    </Route>
                    <Route element={<RequirePermission codes={["visits.view"]} label="Visit notes" />}>
                      <Route path="/visits/:visitId" element={<VisitNotesPage />} />
                    </Route>
                    {/* Roster is read by anyone who may see doctors or keep their
                        own schedule (the Doctor template has the latter only). */}
                    <Route element={<RequirePermission codes={["doctors.view", "doctors.manage_schedule", "doctors.update_status"]} label="Roster" />}>
                      <Route path="/roster" element={<RosterPage />} />
                    </Route>
                  </Route>
                  {/* Patients: clinic staff only, AND only with the grant. The
                      gate is here rather than inside each page so a revoked
                      patients.view blocks a typed URL and a deep link too — not
                      just the sidebar entry. Editing no longer has a route of its
                      own — it is a drawer over the profile — so patients.update is
                      enforced on the Edit button, inside the drawer, and by the
                      PATCH server-side. /patients/:id/edit only redirects now, so
                      it sits under view: gating it on update would deny an old
                      bookmark to a user who is perfectly allowed to see the
                      profile it lands on. */}
                  <Route element={<RequirePermission codes={["patients.view"]} label="Patients" />}>
                    <Route path="/patients" element={<PatientListPage />} />
                    <Route path="/patients/:id" element={<PatientProfilePage />} />
                    <Route path="/patients/:id/edit" element={<RedirectToPatientProfile />} />
                  </Route>
                  {/* Vitals trend is clinical data — Admin/Doctor only (Receptionist = No). */}
                  <Route element={<RequireRoles roles={["Admin", "Doctor"]} />}>
                    <Route element={<RequirePermission codes={["visits.view"]} label="Vitals" />}>
                      <Route path="/vitals/trend" element={<VitalsTrendPage />} />
                    </Route>
                  </Route>
                  {/* Doctor Management follows View Doctors, the Doctors
                      counterpart of View Patients — it used to be users.role ==
                      Admin, so neither unticking a Doctors permission nor granting
                      one to a custom role changed anything. Add/Edit/Delete inside
                      follow Manage Doctors. Legacy fallback keeps it Admin-only. */}
                  <Route element={<RequirePermission codes={["doctors.view"]} label="Doctor Management" legacyRoles={["Admin"]} />}>
                    <Route path="/doctors" element={<DoctorListPage />} />
                  </Route>
                  {/* Every appointment screen reads the schedule, and the four
                      GETs behind them now require appointments.view. A booking-only
                      role (create without view) can still open the Book Appointment
                      modal from the sidebar — /slots admits either grant — it just
                      cannot browse the schedule afterwards. */}
                  <Route element={<RequirePermission codes={["appointments.view"]} label="Appointments" />}>
                    <Route path="/appointments" element={<AppointmentsPage />} />
                    {/* Bulk reschedule moves every appointment in the range —
                        a Manage Appointments action, not a view. */}
                    <Route element={<RequirePermission codes={["appointments.manage"]} label="Bulk reschedule" />}>
                      <Route path="/appointments/bulk-reschedule" element={<BulkRescheduleScreen />} />
                    </Route>
                    <Route path="/appointments/calendar" element={<AppointmentCalendarPage />} />
                    <Route path="/appointments/confirmation/:id" element={<AppointmentConfirmationPage />} />
                    <Route path="/appointments/queue" element={<ReceptionistQueuePage />} />
                  </Route>
                  {/* Pharmacy module not built yet — placeholder coming-soon page. */}
                  <Route path="/pharmacy" element={<PharmacyComingSoonPage />} />
                  <Route path="/settings" element={<SettingsPage />} />
                  <Route path="/settings/export/:jobId" element={<ExportJobStatusPage />} />
                  <Route element={<RequirePermission codes={["billing.write"]} label="Billing" />}>
                    <Route path="/billing" element={<CreateBillPage />} />
                  </Route>
                  {/* Doctor prescription history */}
                  <Route element={<RequireRoles roles={["Admin", "Doctor"]} />}>
                    <Route element={<RequirePermission codes={["visits.view"]} label="Prescriptions" />}>
                      <Route path="/my-prescriptions" element={<DoctorPrescriptionsPage />} />
                    </Route>
                    <Route path="/immunizations/cohort" element={<CohortReminderPage />} />
                  </Route>
                  </Route>{/* RequireClinicSelection */}
                </Route>
              </Route>

              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              <Route path="*" element={<Navigate to="/login" replace />} />
            </Routes>
            </div>
          </BrowserRouter>
        </AddPatientProvider>
      </AddDoctorProvider>
    </BookAppointmentProvider>
    </SidebarProvider>
    </ClinicProvider>
    </ImportProgressProvider>
  );
};

export default App;